import React, { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import Modal from "./Modal";
import Select from "./Select";
import { useData } from "../context/DataContext";

// Editor modal for add/edit tenant. Handles the occupied-house conflict flow.
// Props:
//  open, onClose
//  editingTenant: tenant | null
//  defaultPropertyId: string (used for add)
//  lockedPropertyId: string | null (hides property select when set)
export default function TenantEditor({
  open,
  onClose,
  editingTenant,
  defaultPropertyId,
  lockedPropertyId = null,
}) {
  const { properties, addTenant, updateTenant, vacateTenant, findActiveAt, logDepositChange } =
    useData();
  const [form, setForm] = useState(emptyForm(defaultPropertyId));
  const [conflict, setConflict] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    if (editingTenant) {
      setForm({
        name: editingTenant.name,
        phone: editingTenant.phone,
        houseNumber: editingTenant.houseNumber,
        rent: editingTenant.rent,
        dueDay: editingTenant.dueDay || 5,
        deposit: editingTenant.deposit || "",
        propertyId: editingTenant.propertyId,
      });
    } else {
      setForm(emptyForm(lockedPropertyId || defaultPropertyId));
    }
    setConflict(null);
    setError("");
  }, [open, editingTenant, defaultPropertyId, lockedPropertyId]);

  const submit = (e) => {
    e.preventDefault();
    setError("");

    if (!form.propertyId) {
      setError("Please choose a property");
      return;
    }
    if (!form.name.trim()) {
      setError("Full name is required");
      return;
    }
    if (!String(form.phone).trim()) {
      setError("Phone number is required");
      return;
    }
    if (!String(form.houseNumber).trim()) {
      setError("House number is required");
      return;
    }
    const rentNum = Number(form.rent);
    if (!form.rent && form.rent !== 0) {
      setError("Rent amount is required");
      return;
    }
    if (Number.isNaN(rentNum) || rentNum <= 0) {
      setError("Rent must be a positive number");
      return;
    }
    const dueDayNum = Number(form.dueDay);
    if (!form.dueDay && form.dueDay !== 0) {
      setError("Rent due day is required");
      return;
    }
    if (Number.isNaN(dueDayNum) || dueDayNum < 1 || dueDayNum > 31) {
      setError("Rent due day must be between 1 and 31");
      return;
    }

    const existing = findActiveAt(
      form.propertyId,
      form.houseNumber,
      editingTenant?.id || null
    );
    if (existing) {
      setConflict({ existing });
      return;
    }
    commit();
  };

  const commit = async () => {
    const payload = {
      ...form,
      rent: Number(form.rent) || 0,
      dueDay: Math.min(31, Math.max(1, Number(form.dueDay) || 1)),
      deposit: Number(form.deposit) || 0,
    };
    setSaving(true);
    try {
      if (editingTenant) {
        await updateTenant(editingTenant.id, payload);
      } else {
        await addTenant(payload);
      }
      onClose();
    } catch (err) {
      // Error will show via the calling component
    } finally {
      setSaving(false);
    }
  };

  const onVacateAndReplace = () => {
    if (!conflict) return;
    vacateTenant(conflict.existing.id);
    commit();
    setConflict(null);
  };

  const propertyOptions = properties.map((p) => ({ value: p.id, label: p.name }));

  return (
    <>
      <Modal
        open={open && !conflict}
        onClose={onClose}
        title={editingTenant ? "Edit tenant" : "Add tenant"}
        description="Tenant details and house assignment."
      >
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="label-base">Full name</label>
            <input
              className="input-base"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
            />
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className="label-base">Phone</label>
              <input
                className="input-base"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="label-base">House number</label>
              <input
                className="input-base"
                value={form.houseNumber}
                onChange={(e) => setForm({ ...form, houseNumber: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="label-base">Rent (KES / month)</label>
              <input
                type="number"
                min="1"
                className="input-base"
                value={form.rent}
                onChange={(e) => setForm({ ...form, rent: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="label-base">Rent due on day</label>
              <input
                type="number"
                min="1"
                max="31"
                className="input-base"
                value={form.dueDay}
                onChange={(e) => setForm({ ...form, dueDay: e.target.value })}
                required
              />
              <p className="mt-1 text-xs text-gray-500">Day of each month</p>
            </div>
            <div className="sm:col-span-2">
              <label className="label-base">Deposit paid (KES)</label>
              <input
                type="number"
                className="input-base"
                value={form.deposit}
                onChange={(e) => setForm({ ...form, deposit: e.target.value })}
                placeholder="0"
              />
            </div>
          </div>
          {!lockedPropertyId && (
            <div>
              <label className="label-base">Property</label>
              <Select
                value={form.propertyId}
                onChange={(v) => setForm({ ...form, propertyId: v })}
                options={propertyOptions}
                placeholder="Choose property"
              />
            </div>
          )}
          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-400">
              {error}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              {editingTenant ? "Save changes" : "Add tenant"}
            </button>
          </div>
        </form>
      </Modal>

      <Modal
        open={!!conflict}
        onClose={() => setConflict(null)}
        title={conflict ? `House ${conflict.existing.houseNumber} is currently occupied` : ""}
        description={
          conflict
            ? `${conflict.existing.name} is the current ACTIVE tenant. How would you like to proceed?`
            : ""
        }
      >
        <div className="space-y-2">
          <button
            className="w-full rounded-lg border border-gray-200 dark:border-gray-600 px-4 py-3 text-left text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700"
            onClick={onVacateAndReplace}
          >
            <div className="text-gray-900 dark:text-white">Vacate & Replace Tenant</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              Mark {conflict?.existing.name} as VACATED, then {editingTenant ? "save the changes" : "add the new tenant"}.
            </div>
          </button>
          <button
            className="w-full rounded-lg border border-gray-200 dark:border-gray-600 px-4 py-3 text-left text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700"
            onClick={() => setConflict(null)}
          >
            <div className="text-gray-900 dark:text-white">Choose Different House</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">Return to the form and pick another house.</div>
          </button>
          <button
            className="w-full rounded-lg px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700"
            onClick={() => {
              setConflict(null);
              onClose();
            }}
          >
            Cancel
          </button>
        </div>
      </Modal>
    </>
  );
}

function emptyForm(propertyId) {
  return {
    name: "",
    phone: "",
    houseNumber: "",
    rent: "",
    dueDay: 5,
    deposit: "",
    propertyId: propertyId || "",
  };
}
